import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FileButton,
  Group,
  Image,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconPhoto,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { surgeryCentersLabsService } from "../../../services/admin/surgeryCentersLabs.service";

interface SurgeryCenterLabLogoUploadProps {
  currentLogoS3Key?: string | null;
  onLogoChange: (s3Key: string | null) => void;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES = ["image/png", "image/jpeg"];

export function SurgeryCenterLabLogoUpload({
  currentLogoS3Key,
  onLogoChange,
}: SurgeryCenterLabLogoUploadProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);

  // Fetch a signed download URL for an existing logo key to show a preview
  useEffect(() => {
    if (!currentLogoS3Key) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    surgeryCentersLabsService
      .getLogoDownloadUrl(currentLogoS3Key)
      .then((url) => {
        if (!cancelled) setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentLogoS3Key]);

  const handleFileSelect = async (file: File | null) => {
    if (!file) return;

    setError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Only PNG and JPEG files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("File size must be less than 2 MB.");
      return;
    }

    setUploading(true);
    try {
      const { url, key } = await surgeryCentersLabsService.getLogoPresignedUrl(
        file.name,
        file.type
      );
      await surgeryCentersLabsService.uploadLogoToS3(url, file);

      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      onLogoChange(key);

      notifications.show({
        title: "Logo uploaded",
        message: "Surgical center logo uploaded successfully.",
        color: "green",
      });
    } catch (err: any) {
      notifications.show({
        title: "Upload failed",
        message: err?.message || "Could not upload logo. Please try again.",
        color: "red",
      });
    } finally {
      setUploading(false);
      resetRef.current?.();
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setError(null);
    onLogoChange(null);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  };

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <IconPhoto size={16} />
        <Text fw={600} size="sm">
          Surgical Center Logo
        </Text>
      </Group>
      <Text size="xs" c="dimmed">
        This logo will appear in the report header for pass-through cases sent
        to this surgical center.
      </Text>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {previewUrl ? (
        <Box style={{ position: "relative", display: "inline-block" }}>
          <Image
            src={previewUrl}
            alt="Surgical center logo preview"
            fit="contain"
            style={{
              maxHeight: 100,
              maxWidth: 300,
              border: "1px solid var(--mantine-color-gray-3)",
              borderRadius: 8,
              padding: 8,
              backgroundColor: "#fff",
            }}
            onError={() => setPreviewUrl(null)}
          />
          <Button
            variant="filled"
            color="red"
            size="xs"
            leftSection={<IconX size={14} />}
            onClick={handleRemove}
            style={{ position: "absolute", top: 6, right: 6 }}
          >
            Remove
          </Button>
        </Box>
      ) : (
        <Box
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            border: isDragging
              ? "2px dashed var(--mantine-color-blue-5)"
              : "2px dashed var(--mantine-color-gray-4)",
            borderRadius: 8,
            padding: "24px 32px",
            textAlign: "center",
            backgroundColor: isDragging
              ? "var(--mantine-color-blue-0)"
              : "var(--mantine-color-gray-0)",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <Stack gap="sm" align="center">
            <IconUpload
              size={32}
              color={
                isDragging
                  ? "var(--mantine-color-blue-5)"
                  : "var(--mantine-color-gray-6)"
              }
            />
            <div>
              <Text size="sm" fw={500} mb={2}>
                {isDragging
                  ? "Drop logo here"
                  : "Drop logo here or click to browse"}
              </Text>
              <Text size="xs" c="dimmed">
                PNG or JPEG, max 2 MB
              </Text>
            </div>
            <FileButton
              resetRef={resetRef}
              onChange={handleFileSelect}
              accept="image/png,image/jpeg"
            >
              {(props) => (
                <Button
                  {...props}
                  variant="light"
                  size="sm"
                  leftSection={<IconUpload size={14} />}
                  loading={uploading}
                >
                  Choose File
                </Button>
              )}
            </FileButton>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
